import type { Project } from "@/types";

/**
 * Project status as displayed in the UI. Derived from lifecycle + run-readiness — never an
 * opinion the user asserts.
 *
 * - "Setup Required" → the project isn't run-ready (a wizard, connection, or datastore gap).
 *   Beats Active for display because it's actionable (something for the user to do).
 * - "Archived" / "Deleted" → from the lifecycle status (`ProjectStatus` enum on the backend:
 *   Draft | Active | Archived | Deleted). Draft/Active never surface directly — see getProjectStatus.
 */
export type ProjectStatusLabel = "Setup Required" | "Active" | "Archived" | "Deleted" | "Error";

/**
 * Resolve a project's display status. `isReady` is a derived flag the backend supplies
 * (true = no blocking configuration gaps across wizard, connections, and datastores). When it
 * isn't available (e.g. before the readiness query resolves), we fall back to lifecycle-only.
 *
 * Only `Deleted` and `Archived` are meaningful lifecycle states in the running app —
 * `Draft`/`Active` have no real transition (nothing calls `Project.Activate()`; a normally-created
 * project is `Draft` forever) and gate no behaviour, so they'd only ever surface a confusing
 * "Draft" on a fully-configured project. Readiness is the signal that matters, so anything that
 * isn't Deleted/Archived resolves to Setup Required (not ready) or Active (ready).
 */
export function getProjectStatus(
  project: Project | null | undefined,
  isReady?: boolean | null
): ProjectStatusLabel {
  if (!project) return "Setup Required";

  // Deleted/Archived are the only lifecycle states that carry meaning — honour them first.
  const lifecycle = (project.status ?? "").toLowerCase();
  switch (lifecycle) {
    case "deleted":
      return "Deleted";
    case "archived":
      return "Archived";
    case "error":
      return "Error";
  }

  // Otherwise readiness is the display status. Only treat the flag as "not ready" when the backend
  // told us something definitive (boolean false vs. undefined) — an unresolved query reads as Active
  // rather than loudly implying a problem that isn't there.
  return isReady === false ? "Setup Required" : "Active";
}

/**
 * Bootstrap badge class for the status label.
 */
export function getProjectStatusClass(label: ProjectStatusLabel): string {
  switch (label) {
    case "Active":
      return "bg-success";
    case "Setup Required":
      return "bg-warning text-dark";
    case "Archived":
      return "bg-secondary";
    case "Deleted":
      return "bg-dark";
    case "Error":
      return "bg-danger";
  }
}
